import type { LifespanStep } from '../../_common/types.js';
import { Step } from '../enums.js';

/**
 * The fields `isUnfilledSeededVersion` discriminates on. A structural subset of
 * `ContentVersion` so both the full version entity / DTO and slimmer carriers
 * (e.g. the FE preview projection in `content-version.entity.ts`) can call the
 * one predicate without constructing a whole `ContentVersion`.
 */
export interface SeededVersionDiscriminator {
  changeSummary: string;
  versionNumber: number;
  createdAtPosition: Pick<LifespanStep, 'step'>;
}

/**
 * Title the `round_focus` tool stamps on the empty version it seeds for an
 * assignee to fill during Write. The `content_title` column carries a
 * `CHECK (char_length >= 1)`, so the seed cannot use an empty string; this
 * sentinel stands in until the first real Write overwrites it. Imported by
 * `round-focus.tool.ts` (the producer) and by `isUnfilledSeededVersion` (the
 * consumers) so the producer and the predicate can never drift apart.
 */
export const SEEDED_VERSION_TITLE_PLACEHOLDER = '(empty)';

const PLACEHOLDER_TITLE_PATTERN = /^\(\s*empty\s*\)$/i;

export function isPlaceholderContentTitle(title: string | null | undefined): boolean {
  if (title == null) return false;
  const trimmedTitle = title.trim();
  return (
    trimmedTitle === SEEDED_VERSION_TITLE_PLACEHOLDER ||
    PLACEHOLDER_TITLE_PATTERN.test(trimmedTitle)
  );
}

/**
 * True when a version carries no authored body yet. The one definition of
 * "this version has nothing to read": the `round_focus` seed writes an empty
 * `content`, and the product card reads this through
 * `ProductSummary.hasEmptyLatestVersion` to say a draft is still being written
 * rather than showing a version mark for a document that does not exist yet.
 */
export function isEmptyVersionContent(input: { content: string }): boolean {
  return input.content.trim().length === 0;
}

/**
 * Steps at which the chair seeds a version. `round_focus` is `exposedTo`
 * `[Bootstrap, PlanAssign]` only, and operatives never seed at a planning step
 * (they author content at Write). So a version stamped with the seeded shape AND
 * created at a planning step is a placeholder seeded by the chair on behalf of
 * the assignee, whose member id is now the version's authorMember. The predicate
 * stays entity-only (no session-base / role lookup): it keys on the created-at
 * step, so seeded author ownership does not change its behavior.
 */
const SEEDED_VERSION_PLANNING_STEPS: ReadonlySet<Step> = new Set<Step>([
  Step.Bootstrap,
  Step.PlanAssign,
]);

/**
 * True when `version` is a planning-step seeded placeholder no operative has
 * filled yet.
 *
 * Discriminator (all must hold):
 * - `changeSummary.trim() === ''` — no write landed. The seed writes the empty
 *   sentinel; the content-version tools require a non-empty TLDR for every real
 *   write.
 * - `versionNumber >= 1` — the placeholder can be a first version or revision.
 * - `createdAtPosition.step ∈ {Bootstrap, PlanAssign}` — created by a planning
 *   seed, independent of which member owns the version row.
 *
 * Content and title are deliberately not part of this predicate: revision seeds
 * carry a copied body and title by design. A genuinely-empty operative draft is
 * excluded because it is authored at Write, not at a planning step.
 */
export function isUnfilledSeededVersion(input: { version: SeededVersionDiscriminator }): boolean {
  const { version } = input;
  return (
    version.changeSummary.trim() === '' &&
    version.versionNumber >= 1 &&
    SEEDED_VERSION_PLANNING_STEPS.has(version.createdAtPosition.step)
  );
}

/**
 * Floor for the write-focus completion threshold (issue 006). A product is
 * "filled" only once its latest version carries at least this many trimmed
 * characters. `contentMinLength` raises the bar when the chair set one; the
 * floor guarantees the empty seed never clears the gate even when no minimum
 * was configured. Keep as a named const (no Env) so the threshold lives in one
 * place.
 */
export const WRITE_FOCUS_MIN_CONTENT_LENGTH = 1;

/** The product fields `writeFocusMinLength` reads. */
export interface WriteFocusProductDiscriminator {
  contentMinLength: number | null;
}

/** The version fields `writeFocusContentLength` reads. */
export interface WriteFocusVersionDiscriminator {
  content: string;
}

/**
 * Effective minimum trimmed-character count a write-focused product's latest
 * version must reach to count as filled (issue 006). `Math.max(contentMinLength
 * ?? 1, 1)` — the chair-set minimum when present, never below the seed-rejecting
 * floor.
 */
export function writeFocusMinLength(product: WriteFocusProductDiscriminator): number {
  return Math.max(product.contentMinLength ?? WRITE_FOCUS_MIN_CONTENT_LENGTH, WRITE_FOCUS_MIN_CONTENT_LENGTH);
}

/** Measured trimmed-character length of a version's content (issue 006). */
export function writeFocusContentLength(version: WriteFocusVersionDiscriminator): number {
  return version.content.trim().length;
}

/**
 * The single definition of "a write-focused product's latest version is
 * filled" (issue 006, Option D). True when the latest version exists, is not an
 * unfilled planning-step seed, and its trimmed content meets the product's
 * effective minimum (`writeFocusMinLength`).
 *
 * `latestVersion === null` ⇒ false: a product with no version (or whose latest
 * was cleared) is not filled. A planning-step seeded placeholder is also
 * incomplete even when it carries copied revision content; only a real write flips
 * `isUnfilledSeededVersion` false.
 *
 * Shared so the submit-gate content check and the durable post-commit backstop
 * apply the identical rule.
 */
export function isWriteFocusComplete(input: {
  product: WriteFocusProductDiscriminator;
  latestVersion: (WriteFocusVersionDiscriminator & SeededVersionDiscriminator) | null;
}): boolean {
  if (input.latestVersion === null) return false;
  if (isUnfilledSeededVersion({ version: input.latestVersion })) return false;
  return writeFocusContentLength(input.latestVersion) >= writeFocusMinLength(input.product);
}
